<!-- @format -->
<template>
    <div class="custom-field-widget">
        <div class="widget-header">
            <div class="header-text">
                <h3>{{ widgetTitle }}</h3>
            </div>
            <div class="svg-container widget-close-button" @click="closeWidget">
                <widget-close-svg />
            </div>
        </div>
        <div class="content-wrapper">
            <div v-if="isLoading" class="loading-screen">
                <loading-screen-svg />
            </div>
            <div v-else>
                <div class="select-step" v-if="currentStep === 'select'">
                    <custom-field-type-container
                        v-for="component in displayedInputTypeOptions"
                        class="type-container"
                        :key="component.id"
                        :type="component.name"
                    />
                </div>
                <div class="create-step" v-if="currentStep === 'create'">
                    <div>
                        <label class="input-label">Field Type</label>
                        <custom-field-type-dropdown
                            v-model="form.inputType"
                            placeholder="Field Type"
                            :items="inputTypeOptions"
                            :disabled="isEditMode"
                            @input="handleInputType"
                        />
                    </div>
                    <app-input v-model="form.name" label="Field Name" placeholder="Field Name" />
                    <app-input
                        v-model="form.description"
                        label="Description"
                        placeholder="Write a description"
                    />

                    <div v-if="isCheckbox">
                        <div class="field-value-input">
                            <app-input
                                v-model="form.value"
                                label="Checkbox label"
                                placeholder="Type in checkbox label"
                            />
                            <app-button
                                v-if="fieldInputType === 'Multiple Checkboxes'"
                                class="add-value-btn"
                                @click="handleClickAddValue"
                                :disabled="!form.value"
                            >
                                <o6-add-svg />
                            </app-button>
                        </div>
                        <div
                            v-if="fieldInputType === 'Multiple Checkboxes'"
                            class="field-checkboxes-values"
                        >
                            <div
                                v-for="(item, index) in form.inputValue"
                                :key="index"
                                class="checkbox-value-chip"
                            >
                                <span class="chip-value">{{ item }}</span>
                                <app-button @click="handleClickRemoveItem(item)">
                                    <s3-close-svg />
                                </app-button>
                            </div>
                        </div>
                    </div>
                    <div v-if="isDropdown">
                        <div class="field-value-input">
                            <div
                                v-if="fieldInputType === 'Dropdown'"
                                class="field-dropdown-values"
                            >
                                <draggable
                                    :list="form.inputValue"
                                    handle=".handle .h8-drag-svg"
                                    ghost-class="ghost"
                                    chosen-class="chosen"
                                    @start="dragging = true"
                                    @end="dragging = false"
                                >
                                    <div
                                        v-for="(option, index) in form.inputValue"
                                        :key="option.id"
                                    >
                                        <div class="handleContainer">
                                            <div class="handle">
                                                <h8-drag-svg />
                                                <label>{{ `Dropdown Option ${index + 1}` }}</label>
                                                <n15-unsuscribe-svg
                                                    class="remove-option-btn"
                                                    v-show="showRemove"
                                                    color="#F30070"
                                                    @click.native="handleClickRemoveItem(option)"
                                                />
                                            </div>
                                            <div class="option-wrapper">
                                                <app-input
                                                    v-model="form.inputValue[index]"
                                                    class="option-label-input"
                                                    :max-length="2000"
                                                    :data-locator="`dropdown-option-${index + 1}`"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </draggable>
                                <app-button
                                    class="add-option-btn"
                                    @click="handleClickAddDropdown"
                                    :disabled="isAddDropdownOptionDisabled"
                                >
                                    Add Option<o6-add-svg />
                                </app-button>
                            </div>
                        </div>
                    </div>
                    <custom-field-table-preview :form="form" />
                    <custom-field-field-preview :form="form" />
                </div>
            </div>
        </div>
        <div class="widget-footer">
            <div class="cancel-container">
                <app-button class="cancel-btn transparent-btn" size="normal" @click="closeWidget">
                    Cancel
                </app-button>
            </div>
            <div class="action-container">
                <app-button
                    :disabled="!isFormValid"
                    class="next-btn"
                    @click="!isEditMode ? createCustomField() : updateCustomField()"
                >
                    Save
                </app-button>
            </div>
        </div>
    </div>
</template>

<script>
import EventBus from '@/scripts/event-bus.js';
import { required, requiredIf } from 'vuelidate/lib/validators';

const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg.vue');
const AppButton = () => import('@/components/common/app-button.vue');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');
const H8DragSvg = () => import('@/components/svg/grid/h8-drag-svg');
const N15UnsuscribeSvg = () => import('@/components/svg/grid/n15-unsuscribe-list-svg');
const AppInput = () => import('@/components/common/app-input');
const AppDropdown = () => import('@/components/common/app-dropdown');
const O6AddSvg = () => import('@/components/svg/grid/o6-add-svg');
const S3CloseSvg = () => import('@/components/svg/grid/s3-close-svg');
const AppBorderSection = () => import('@/components/common/app-border-section');
const AppToolTip = () => import('@/components/common/app-tooltip');
const AppInfoIconPopup = () => import('@/components/popups/app-info-icon-popup');
const BoardTableHeaderCell = () => import('@/components/board/board-table-header-cell');
const R7EditV2Svg = () => import('@/components/svg/grid/r7-edit-v2-svg');
const AppDatePicker = () => import('@/components/common/app-date-picker');
const AppTextArea = () => import('@/components/common/app-textarea');
const AppCheckBox = () => import('@/components/common/app-checkbox');
const CustomFieldTablePreview = () =>
    import('@/components/sliding-widgets/custom-field-reusable/custom-field-table-preview');
const CustomFieldFieldPreview = () =>
    import('@/components/sliding-widgets/custom-field-reusable/custom-field-field-preview');
const CustomFieldTypeContainer = () =>
    import('@/components/sliding-widgets/custom-field-reusable/custom-field-type-container');
const CustomFieldTypeDropdown = () =>
    import('@/components/sliding-widgets/custom-field-reusable/custom-field-type-dropdown');

import draggable from 'vuedraggable';

export default {
    name: 'custom-field-widget',

    components: {
        'widget-close-svg': WidgetCloseSvg,
        'app-button': AppButton,
        'loading-screen-svg': LoadingScreenSvg,
        'h8-drag-svg': H8DragSvg,
        'n15-unsuscribe-svg': N15UnsuscribeSvg,
        'app-input': AppInput,
        'app-dropdown': AppDropdown,
        'o6-add-svg': O6AddSvg,
        's3-close-svg': S3CloseSvg,
        'app-border-section': AppBorderSection,
        'app-tooltip': AppToolTip,
        'app-info-icon-popup': AppInfoIconPopup,
        'board-table-header-cell': BoardTableHeaderCell,
        'r7-edit-v2-svg': R7EditV2Svg,
        'app-date-picker': AppDatePicker,
        'app-text-area': AppTextArea,
        'app-checkbox': AppCheckBox,
        'custom-field-table-preview': CustomFieldTablePreview,
        'custom-field-field-preview': CustomFieldFieldPreview,
        'custom-field-type-container': CustomFieldTypeContainer,
        'custom-field-type-dropdown': CustomFieldTypeDropdown,
        draggable: draggable,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            isLoading: false,
            form: {
                name: '',
                description: '',
                inputType: null,
                inputValue: [],
                value: '',
            },

            inputTypeOptions: [
                { id: 1, name: 'Single-line Text', value: 'single-line text' },
                { id: 2, name: 'Multi-line Text', value: 'multi-line text' },
                { id: 3, name: 'Date Picker', value: 'date picker' },
                { id: 4, name: 'Single Checkbox', value: 'single checkbox' },
                { id: 5, name: 'Multiple Checkboxes', value: 'multiple checkboxes' },
                { id: 6, name: 'Number', value: 'number' },
                { id: 7, name: 'URL', value: 'url' },
                { id: 8, name: 'Dropdown', value: 'single dropdown'},
            ],
            currentStep: 'select',
        };
    },

    validations() {
        return {
            form: {
                name: {
                    required,
                },

                description: {
                    required,
                },

                inputType: {
                    required,
                },

                inputValue: {
                    required: requiredIf(() => {
                        return this.fieldInputType === 'Multiple Checkboxes' || this.fieldInputType === 'Dropdown';
                    }),
                    noEmptyStrings: function(value) {
                        if (!Array.isArray(value)) return false;
                        return value.every(item => item !== '');
                    }
                },

                value: {
                    required: requiredIf(() => {
                        return this.fieldInputType === 'Single Checkbox';
                    }),
                },
            },
        };
    },

    computed: {
        widgetTitle() {
            return this.widget.options.type === 'AgencyClient'
                ? 'Custom Partner Fields'
                : `Custom ${this.widget.options.type} Fields`;
        },

        widgetType() {
            return this.widget.options.type;
        },

        clientId() {
            return this.widget.options.clientId;
        },

        agencyAccountId() {
            return this.widget.options.agencyAccountId;
        },

        isFormValid() {
            return !this.$v.$invalid;
        },

        fieldInputType() {
            return this.form.inputType ? this.form.inputType.name : '';
        },

        isEditMode() {
            return !!this.widget.customFieldId;
        },

        isCheckbox() {
            return this.fieldInputType.includes('Checkbox');
        },

        isDropdown() {
            return this.fieldInputType.includes('Dropdown');
        },

        isAddDropdownOptionDisabled() {
            return this.form.inputValue.length > 0 && this.form.inputValue.some((value) => value === '');
        },

        postUrl() {
            if (this.widgetType === 'AgencyClient') {
                return `/ms/custom-fields/v1/custom${this.widgetType}Fields/account/${this.agencyAccountId}`;
            } else {
                return `/ms/custom-fields/v1/custom${this.widgetType}Fields/${this.clientId}`;
            }
        },

        updateUrl() {
            const { customFieldId } = this.widget;

            if (this.widgetType === 'AgencyClient') {
                return `/ms/custom-fields/v1/custom${this.widgetType}Fields/${customFieldId}/account/${this.agencyAccountId}`;
            } else {
                return `/ms/custom-fields/v1/custom${this.widgetType}Fields/${customFieldId}/${this.clientId}`;
            }
        },

        showRemove() {
            return this.form.inputValue.length > 1;
        },

        displayedInputTypeOptions() {
            if(this.widget.options.type === 'Opportunity') {
                return this.inputTypeOptions.filter((option) => option.value !== 'single dropdown');
            }

            return this.inputTypeOptions;
        }
    },

    created() {
        if (this.isEditMode) {
            this.getEditCustomField();
        } else if (this.widget.options.customFieldType) {
            this.setType({ type: this.widget.options.customFieldType });
        }

        EventBus.$on('select-custom-field-type', this.setType);
    },

    beforeDestroy() {
        EventBus.$off('select-custom-field-type', this.setType);
    },

    methods: {
        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        handleInputType() {
            // needed for UX
            if (this.fieldInputType === 'Dropdown') {
                this.form.inputValue = [''];
            } else {
                this.form.inputValue = [];
            }
            this.form.value = '';
        },

        addCustomFieldValue() {
            if (this.fieldInputType === 'Single Checkbox') {
                this.form.inputValue = [this.form.value];
            }
        },

        submitCustomFieldForm() {
            return new Promise((resolve, reject) => {
                const customField = {
                    Name: this.form.name,
                    Description: this.form.description,
                    InputType: this.form.inputType.value,
                    Values: this.form.inputValue,
                };

                if (this.isEditMode) {
                    const { customFieldId } = this.widget;
                    this.$api.put(this.updateUrl, customField).then(resolve).catch(reject);
                } else {
                    this.$api.post(this.postUrl, customField).then(resolve).catch(reject);
                }
            });
        },

        updateCustomField() {
            this.addCustomFieldValue();
            this.submitCustomFieldForm()
                .then(() => {
                    this.$helpers.showSuccessNotification('Custom field updated successfully.');
                    EventBus.$emit('custom-field:update');
                    this.closeWidget();
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred trying to update the custom field.',
                        { error },
                    );
                });
        },

        createCustomField() {
            this.isLoading = true;
            this.addCustomFieldValue();
            this.submitCustomFieldForm()
                .then(() => {
                    this.$helpers.showSuccessNotification('Custom field created successfully.');
                    EventBus.$emit('custom-field:update');
                    this.closeWidget();
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred trying to create the custom field.',
                        { error },
                    );
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },

        getEditCustomField() {
            this.isLoading = true;

            this.$api
                .get(this.updateUrl)
                .then(this.setEditCustomFieldData)
                .catch((error) => {
                    this.$helpers.showFailNotification('Failed to get custom field info', { error });
                })
                .finally(() => {
                    this.currentStep = 'create';
                    this.isLoading = false;
                });
        },

        setEditCustomFieldData({ data }) {
            const item = data.data;
            this.form = {
                ...this.form,
                name: item.Name,
                description: item.Description,
                inputType: this.getInputType(item.InputType),
                inputValue: this.getInputValue(item.CustomDataFields),
                value: this.getValue(item.CustomDataFields, item.InputType),
            };
        },

        getInputType(name) {
            return this.inputTypeOptions.find((option) => option.name.toLowerCase() === name || option.value.toLowerCase() === name);
        },

        getInputValue(values) {
            return values.map((value) => value.Name);
        },

        getValue(values, type) {
            return type === 'single checkbox' ? values[0].Name : '';
        },

        handleClickAddValue() {
            this.form.inputValue.push(this.form.value.trimRight());
            this.form.value = '';
        },

        handleClickAddDropdown() {
            this.form.inputValue.push('');
        },

        handleClickRemoveItem(item) {
            const updateCheckboxOrDropdownValues = this.form.inputValue.filter((option) => option !== item);
            this.form.inputValue = updateCheckboxOrDropdownValues;
        },

        setType(payload) {
            this.currentStep = 'create';
            this.form.inputType = this.inputTypeOptions.find((e) => e.name === payload.type);
            // needed for ux
            if (this.fieldInputType === 'Dropdown') {
                this.form.inputValue = [''];
            }
        },
    },
};
</script>

<style>
.custom-field-widget .loading-screen {
    text-align: center;
}

.custom-field-widget .content-wrapper {
    padding: 24px;
}

.custom-field-widget .create-step {
    display: flex;
    flex-direction: column;
    row-gap: 24px;
}

.custom-field-widget .text-container {
    position: relative;
    margin-top: 40px;
    font-size: 16px;
}

.custom-field-widget .text-container svg {
    position: absolute;
    top: 0px;
    right: 205px;
}

.custom-field-widget .create-step .input-label {
    font-size: 16px;
    font-weight: 500;
    margin-bottom: 7px;
}

.custom-field-widget .create-step .field-value-input {
    display: flex;
    align-items: flex-end;
    column-gap: 10px;
}

.custom-field-widget .field-value-input .app-input {
    flex-grow: 1;
}

.custom-field-widget .field-value-input .add-value-btn {
    border-radius: 100%;
    padding: 0;
    width: 40px;
}

.custom-field-widget .field-value-input .add-value-btn svg path {
    fill: #ffffff;
}

.custom-field-widget .field-value-input .field-dropdown-values {
    padding: 0;
    width: 100%;
}

.custom-field-widget .field-value-input .field-dropdown-values .handleContainer { 
    padding-bottom: 12px;
    margin-bottom: 12px;
}

.custom-field-widget .field-value-input .field-dropdown-values .handleContainer .handle {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    margin-bottom: 12px;
}

.custom-field-widget .field-value-input .field-dropdown-values .handleContainer .handle .h8-drag-svg {
    cursor: move;
}

.custom-field-widget .field-value-input .field-dropdown-values .handleContainer .handle .h8-drag-svg { 
    margin-left: 8px;
}

.custom-field-widget .field-value-input .field-dropdown-values .handleContainer .handle .h8-drag-svg .a { 
    fill: #382153;
}

.custom-field-widget .field-value-input .field-dropdown-values .handleContainer .handle label {
    font: 500 16px/1em 'Roboto';
    flex-grow: 1; 
    margin: 0 10px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.custom-field-widget .field-value-input .field-dropdown-values .add-option-btn {
    margin-top: 4px;
    padding: 0;
    width: 100%;
    color: #ffffff;
}

.custom-field-widget .field-value-input .add-option-btn svg {
    margin-left: 6px;
}

.custom-field-widget .field-value-input .add-option-btn svg path {
    fill: #ffffff;
}

.custom-field-widget .app-input input {
    height: 40px;
}

.custom-field-widget .app-dropdown .app-select.v-input > .v-input__control > .v-input__slot {
    min-height: 40px;
}

.custom-field-widget .app-dropdown .app-select.v-input .v-input__append-inner {
    margin-top: 8px;
}

.custom-field-widget .transparent-btn {
    background: transparent;
    color: #877d88;
    border: none;
}

.custom-field-widget .transparent-btn:hover {
    background: #e1e0e5;
}

.custom-field-widget .cancel-btn {
    background: transparent;
    color: #382153;
    border: 2px solid #cac6cb;
}

.custom-field-widget .action-container {
    display: flex;
    column-gap: 13px;
}

.custom-field-widget .create-step .field-checkboxes-values {
    display: flex;
    column-gap: 6px;
    row-gap: 6px;
    margin-top: 7px;
    flex-wrap: wrap;
}

.custom-field-widget .create-step .field-checkboxes-values .checkbox-value-chip {
    display: flex;
    justify-content: space-between;
    align-items: center;
    border: 1px solid #cac6cb;
    border-radius: 12px;
    height: 24px;
    min-width: 116px;
    font-size: 12px;
}

.custom-field-widget .create-step .custom-field-table-preview {
    margin-bottom: 8px;
}

.custom-field-widget .checkbox-value-chip .chip-value {
    display: inline-block;
    margin-left: 14px;
}

.custom-field-widget .checkbox-value-chip .app-button {
    position: relative;
    left: 1px;
    background-color: transparent;
    width: 24px;
    height: 24px;
    margin-left: 10px;
    padding: 0;
    border-radius: 50%;
}

.custom-field-widget .checkbox-value-chip .app-button svg {
    width: 9px;
    height: 9px;
}

.custom-field-widget .checkbox-value-chip .app-button:hover {
    background-color: #f5d5e7;
}

.custom-field-widget .select-step .custom-field-type-icon .svg-button {
    width: 40px;
    height: 40px;
}

.custom-field-widget .select-step .custom-field-type-icon svg {
    width: 20px;
    height: 20px;
}

.custom-field-widget .ghost {
  background: #18AEFF !important;
  border-radius: 12px;
}

.custom-field-widget .ghost * {
  opacity: 0;
}

.custom-field-widget .chosen {
  background: #8D8B9B;
  border-radius: 12px;
  padding: 12px 8px 0px 8px;
}


</style>
